import type { MeResponse } from "@ondo/shared";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { claimStudio, fetchClaimableStudio } from "../lib/api.ts";
import { describeApiError } from "../lib/apiError.ts";
import { PATHS } from "../routes/routes.constants.ts";
import { workspacePath } from "../routes/routes.model.ts";
import { CLAIM_STUDIO_COPY } from "./workspace.constants.ts";
import { describeClaimableStudio, studioWorkspaceFor } from "./workspace.model.ts";
import type { ClaimStudioNote } from "./workspace.model.ts";

/**
 * The claim-your-studio banner — OND-7, over OND-6's two routes.
 *
 * OND-8 decision 3: the person who signed up at an address a collector named on
 * an authorship claim is told, ON THE FIRST SCREEN AFTER THEY ARE IN, that a
 * request about their own work is waiting — not by email, and not by going
 * looking. Decision 1: taking the studio's seat is a deliberate, explained step
 * and not a side effect of signing up. This controller is both: it asks the
 * server whether a studio waits at the caller's own address, and offers one
 * button.
 *
 * THE RESPONSE IS THE READ. `POST /me/claim-studio` answers with the whole
 * `MeResponse` because the claim moves `capacities`, so `replaceMe` puts it
 * where every screen looks and the workspaces re-derive on the same render.
 * Then the page navigates to the studio's own workspace — found in that
 * profile by the studio's id — or to the chooser when it is not named there.
 * The controller never decides that the account now holds a studio; it only
 * asks, and goes where the answer points.
 */
export type ClaimStudioView =
  | { kind: "hidden" }
  | { kind: "offered"; note: ClaimStudioNote; busy: boolean; error: string | null };

export function useClaimStudioController(input: { replaceMe: (me: MeResponse) => void }): {
  view: ClaimStudioView;
  claim: () => void;
} {
  const navigate = useNavigate();
  const [note, setNote] = useState<ClaimStudioNote | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    fetchClaimableStudio()
      .then((answer) => {
        if (active) setNote(describeClaimableStudio(answer.studio));
      })
      .catch(() => {
        // A failed read draws nothing. The banner is an offer, and an offer
        // that cannot be confirmed is better absent than guessed at; the
        // inbox beneath it still works.
        if (active) setNote(null);
      });

    return () => {
      active = false;
    };
  }, []);

  const view: ClaimStudioView = note
    ? { kind: "offered", note, busy, error }
    : { kind: "hidden" };

  return {
    view,
    claim: () => {
      if (!note || busy) return;
      setBusy(true);
      setError(null);

      claimStudio()
        .then((me) => {
          input.replaceMe(me);
          const workspace = studioWorkspaceFor(me, note.ondoId);
          void navigate(
            workspace ? workspacePath(workspace, CLAIM_STUDIO_COPY.landing) : PATHS.workspaces,
            { replace: true },
          );
        })
        .catch((cause: unknown) => {
          setError(describeApiError(cause, CLAIM_STUDIO_COPY.failed));
        })
        .finally(() => setBusy(false));
    },
  };
}
